/**
 * Component for Lyrics
 * Component used to render the Lyrics
 *
 * @module components
 */
import { List } from 'immutable';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { LyricsStyles, messages as m } from '../../constants/Lyrics';
import QA from '../../constants/QATags';
import { getInDomain } from '../../domains/externalUtils';
import { renderParagraph } from '../../lib/ArrayUtils';
import { getCurrentListenerFromState } from '../../lib/ListenerUtils';
import { getFullLyricsFromState } from '../../lib/MusicUtils';
import { disableCopy } from '../../lib/StringUtils';
import { isCurrentDominantColorDark } from '../../lib/UIStateUtils';
import { createBEM } from '../../lib/utils/css';
import ButtonT3 from '../lib/ButtonT3/ButtonT3';
import { ButtonT3Theme } from '../lib/ButtonT3/constants';

import './Lyrics.scss';

export class Lyrics extends React.Component {
  static propTypes = {
    artistName: PropTypes.string.isRequired,
    credits: PropTypes.instanceOf(List),
    fullLyrics: PropTypes.instanceOf(List),
    fullLyricsUrl: PropTypes.string,
    getFullLyrics: PropTypes.func.isRequired,
    isBackstage: PropTypes.bool,
    isChildPage: PropTypes.bool,
    isExplicitContentEnabled: PropTypes.bool.isRequired,
    isHeaderVisible: PropTypes.bool.isRequired,
    isLightTheme: PropTypes.bool,
    isNowPlaying: PropTypes.bool,
    isSessionHistoryOpen: PropTypes.bool,
    lyricSnippet: PropTypes.instanceOf(List),
    pandoraId: PropTypes.string.isRequired,
    showFullLyrics: PropTypes.bool,
    songTitle: PropTypes.string.isRequired,
  };

  state = {
    SHOW_FULL_LYRICS_INTENT: false,
    HIDE_FULL_LYRICS: true,
  };

  componentWillMount() {
    if (this.props.showFullLyrics) {
      this.showFullLyrics();
    }
  }

  componentWillReceiveProps(newProps) {
    if (
      this.state.SHOW_FULL_LYRICS_INTENT &&
      (newProps.fullLyrics || this.props.pandoraId !== newProps.pandoraId)
    ) {
      this.setState({
        SHOW_FULL_LYRICS_INTENT: false,
      });
    }
  }

  shouldComponentUpdate(nextProps) {
    return nextProps.isSessionHistoryOpen === this.props.isSessionHistoryOpen;
  }

  componentDidUpdate() {
    if (this.refs.lyrics) {
      // set lyrics div height to height of contents so animation is performed
      this.refs.lyrics.style.height = this.refs.lyricsWrapper.offsetHeight + 'px';
    }
  }

  renderHeader = () => {
    if (this.props.isHeaderVisible) {
      // Duplicate header in session history
      return <div className='Lyrics__header'></div>; // Still want extra padding & border
    }
    return (
      <div className='Lyrics__header'>
        <header className='Lyrics__header__title'>
          <FormattedMessage {...m.lyrics} />
        </header>
      </div>
    );
  };

  showFullLyrics = () => {
    if (this.props.fullLyrics) {
      // It's already been fetched before
      this.setState({
        HIDE_FULL_LYRICS: false,
      });
    } else {
      this.props.getFullLyrics({
        trackPandoraId: this.props.pandoraId,
        returnExplicit: this.props.isExplicitContentEnabled,
      });
      this.setState({
        SHOW_FULL_LYRICS_INTENT: true,
        HIDE_FULL_LYRICS: false,
      });
    }
  };

  renderLyrics = () => {
    if (this.props.fullLyrics && !this.state.HIDE_FULL_LYRICS) {
      return renderParagraph(this.props.fullLyrics);
    }
    return renderParagraph(this.props.lyricSnippet);
  };

  renderFooterContent = () => {
    const lines = [];
    this.props.credits.forEach((credit, index) => {
      lines.push(
        <div className='Lyrics__footer__credits' key={`credit_${index}`}>
          {credit}
        </div>,
      );
    });
    lines.push(
      <div className='Lyrics__footer__credits' key='credit_last'>
        <FormattedMessage {...m.lyricsLicensed} />
      </div>,
    );
    return lines;
  };

  renderFooter = () => {
    return (
      <div className='Lyrics__footer'>
        {this.props.credits && this.renderFooterContent()}
        <span className='Lyrics__footer--bot' />
      </div>
    );
  };

  renderSeeFullLyricsCTA = () => {
    const buttonT3Theme = this.props.isLightTheme ? ButtonT3Theme.LIGHT : ButtonT3Theme.DARK;
    if (this.props.fullLyricsUrl) {
      return (
        <ButtonT3 theme={buttonT3Theme} href={this.props.fullLyricsUrl} data-qa={QA.LSLB}>
          <FormattedMessage {...m.seeAllLyrics} />
        </ButtonT3>
      );
    }
  };

  renderCTA = () => {
    return <div className='Lyrics__divider'>{this.renderSeeFullLyricsCTA()}</div>;
  };

  renderFooterAndCTA = () => {
    if (this.props.fullLyrics && !this.state.HIDE_FULL_LYRICS) {
      return (
        <div>
          {this.renderFooter()}
          {this.renderCTA()}
        </div>
      );
    }
    return this.renderCTA();
  };

  render() {
    if (!this.props.lyricSnippet && !this.props.fullLyrics) return null;

    const classes = createBEM('Lyrics', {
      backstage: this.props.isBackstage,
      fullLyrics: this.props.showFullLyrics,
      lightTheme: this.props.isLightTheme,
      nowPlaying: this.props.isNowPlaying,
    });

    return (
      <div className={classes} key='Lyrics'>
        {!this.props.isBackstage && this.renderHeader()}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div className='Lyrics__lyrics' onMouseUp={disableCopy} ref='lyrics'>
          <div className='Lyrics__lyrics__wrapper' ref='lyricsWrapper'>
            {this.renderLyrics()}
          </div>
        </div>
        {this.renderFooterAndCTA()}
      </div>
    );
  }
}

export function mapStateToProps(state, ownProps) {
  const props = {};
  const listener = getCurrentListenerFromState(state);
  const fullLyrics = getFullLyricsFromState(state, ownProps.pandoraId);
  props.isExplicitContentEnabled = listener && !listener.get('explicitContentFilterEnabled');
  props.isHeaderVisible = props.isNowPlaying ? props.isSessionHistoryOpen : false;

  if (fullLyrics) {
    // The catalog service returns strings, rather arrays of lines like the music service
    props.fullLyrics = `${fullLyrics.get('lyricText')}`.split('\n');
    props.credits = `${fullLyrics.get('credits')}`.split('\n');
  }

  if (ownProps.styleNames) {
    const useLightTheme = !isCurrentDominantColorDark(state);
    props.styleNames = useLightTheme
      ? [...ownProps.styleNames, LyricsStyles.LIGHT_THEME]
      : ownProps.styleNames;
  }

  return props;
}

// eslint-disable-next-line react-redux/mapDispatchToProps-returns-object
export function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      getFullLyrics: getInDomain('catalog.actions.getFullLyrics', noop),
    },
    dispatch,
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(Lyrics);
